import React, { useState, useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import { useAuthUser } from "react-auth-kit"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material"
import { styled } from "@mui/system"
import api from "../../../../utils/api"

interface InstanceData {
  id: string
  title: string
  description: string
  questionCount: number
  questionNum: number
  status: string
}

interface QuestionData {
  id: number
  description: string
  eventId: any
  instanceId: any
  optionCount: number
}

const StyledQuestionButton = styled(Button)({
  margin: "10px",
})

export function SpecificVotingInstancePage() {
  const { id } = useParams<{ id: string }>()
  const [instanceData, setInstanceData] = useState<InstanceData | null>(null)
  const [questions, setQuestions] = useState<QuestionData[]>([])
  const [questionNumber, setQuestionNumber] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const auth = useAuthUser()
  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=The%20voting%20instance%20you%20are%20trying%20to%20access%20does%20not%20exist."
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })
    }
  }, [eventId, id, navigate])

  useEffect(() => {
    const fetchInstanceData = async () => {
      setIsLoading(true)
      try {
        const response = await api.get(`/admin/instances/${id}`)
        setInstanceData(response.data)

        const questionsResponse = await api.get(
          `/admin/instances/${id}/questions`
        )
        const questionsData = questionsResponse.data

        if (questionsData && Array.isArray(questionsData)) {
          // Sort the questions based on created at time
          const sortedQuestions = questionsData.sort((a, b) => {
            const aSeconds = a.createdAt?._seconds || 0
            const bSeconds = b.createdAt?._seconds || 0
            return aSeconds - bSeconds
          })

          setQuestions(sortedQuestions)
          setQuestionNumber(questionsData.length)
        } else {
          console.error(
            "Failed to fetch questions: questionsData is not an array"
          )
        }
      } catch (err) {
        console.error("An error occurred:", err)
        setError("Failed to fetch instance data.")
      } finally {
        setIsLoading(false)
      }
    }

    if (id) {
      fetchInstanceData()
    }
  }, [id])

  const { description, questionCount, status } = instanceData ?? {}

  return (
    <Box padding={3}>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Typography variant="h4">{description}</Typography>
        {status === "open" && (
          <Chip label="Live" color="error" style={{ marginLeft: "10px" }} />
        )}
      </Box>
      <Box my={2}>
        <Typography variant="body1">Question Count: {questionCount}</Typography>

        <Typography variant="body1">
          Status: {toTitleCase(status ?? "")}
        </Typography>
      </Box>

      <Typography variant="h6" m={4}>
        Questions for this instance:
      </Typography>

      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {isLoading ? (
            <CircularProgress />
          ) : questions.length > 0 ? (
            questions.map((question, index) => (
              <StyledQuestionButton
                key={question.id}
                variant="outlined"
                color="primary"
                // Counter based on index or questionNum
              >
                Question #{index + 1}
                <br />
                {question.description}
              </StyledQuestionButton>
            ))
          ) : (
            <Typography variant="body1">No questions currently</Typography>
          )}

          {error && (
            <Alert severity="warning" style={{ width: "50%", margin: "auto" }}>
              {error}
            </Alert>
          )}
        </div>
      </Grid>
      {status === "pre-voting" ? (
        <>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${id}/add-question`}
            style={{ marginTop: "10px", marginRight: "10px" }}
          >
            Add Question
          </Button>

          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${id}/questions/edit`}
            style={{ marginTop: "10px", marginRight: "10px" }}
          >
            Edit Question
          </Button>

          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${id}/questions/delete`}
            style={{ marginTop: "10px", marginRight: "15px" }}
          >
            Delete Question
          </Button>
        </>
      ) : (
        <>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${id}/add-question`}
            style={{ marginTop: "10px", marginRight: "10px" }}
            disabled
          >
            Add Question
          </Button>

          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${id}/questions/edit`}
            style={{ marginTop: "10px", marginRight: "10px" }}
            disabled
          >
            Edit Question
          </Button>

          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/admin/event-admin/instance/${id}/questions/delete`}
            style={{ marginTop: "10px", marginRight: "15px" }}
            disabled
          >
            Delete Question
          </Button>
        </>
      )}
    </Box>
  )
}

const toTitleCase = (str: string) => {
  if (!str) return "" // Safeguard against undefined or null

  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}
